import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import {
  Home, Store, Package, Boxes, ClipboardList, Users, User, TrendingUp, Wheat, MessageCircle,
} from 'lucide-react';

// Bottom-nav vocabulary — one entry per discoverable screen. The role
// map below picks four plus the Home + Profile anchors.
//
// Alerts (bell) is deliberately NOT in this list on any role: the
// header AppBar already carries the bell + unread dot, and we would
// burn a precious tab slot repeating what's one tap away up top.
// See the handover's PR 12 note.
const NAV_ITEMS = {
  home:         { icon: Home,          labelKey: 'nav.home',          path: '/dashboard' },
  profile:      { icon: User,          labelKey: 'nav.profile',       path: '/profile' },
  marketplace:  { icon: Store,         labelKey: 'nav.marketplace',   path: '/marketplace' },
  myFarm:       { icon: Boxes,         labelKey: 'nav.myFarm',        path: '/farm' },
  suppliers:    { icon: Users,         labelKey: 'nav.suppliers',     path: '/suppliers' },
  inventory:    { icon: ClipboardList, labelKey: 'nav.inventory',     path: '/inventory' },
  products:     { icon: Package,       labelKey: 'nav.products',      path: '/products' },
  distributors: { icon: Users,         labelKey: 'nav.distributors',  path: '/distributors' },
  shopkeepers:  { icon: Users,         labelKey: 'nav.shopkeepers',   path: '/shopkeepers' },
  deals:        { icon: TrendingUp,    labelKey: 'nav.deals',         path: '/deals' },
  produce:      { icon: Wheat,         labelKey: 'nav.produce',       path: '/produce' },
  chats:        { icon: MessageCircle, labelKey: 'nav.chats',         path: '/produce/chats' },
};

// Per-role tab order. First entry is always Home, last is always
// Profile; the middle three are chosen for the role's primary
// workflows. PR 7 removed the Cart tab and PR 17 removed the Orders
// tab — the platform has no orders flow on any role. Farmer stocks
// via "Ask my shopkeeper" (Marketplace → Suppliers); the three
// business roles work from their own inventory / products catalog
// and the Deals board, where offers between tiers live.
const ROLE_TABS = {
  farmer:       ['home', 'marketplace', 'myFarm',    'suppliers',    'profile'],
  shopkeeper:   ['home', 'inventory',   'deals',     'distributors', 'profile'],
  distributor:  ['home', 'inventory',   'deals',     'shopkeepers',  'profile'],
  manufacturer: ['home', 'products',    'deals',     'distributors', 'profile'],
  // Farm produce marketplace (PR 67).
  offtaker:     ['home', 'produce',     'chats',     'profile'],
};

const DEFAULT_TABS = ROLE_TABS.farmer;


export default function BottomNav() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const items = useMemo(() => {
    const keys = ROLE_TABS[user?.user_type] || DEFAULT_TABS;
    return keys.map((k) => NAV_ITEMS[k]).filter(Boolean);
  }, [user?.user_type]);

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 safe-area-pb shadow-lg"
      data-testid={`bottom-nav-${user?.user_type || 'default'}`}
    >
      {/* Match the page container width so the five tabs sit under the
          content on desktop instead of hugging the left edge. */}
      <div className="max-w-lg md:max-w-3xl lg:max-w-5xl mx-auto flex justify-around items-center h-16 px-2">
        {items.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={`bottom-nav-item-${item.path.replace(/\//g, '-')}`}
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center justify-center flex-1 h-full py-2 transition-all relative',
                isActive ? 'text-farm-primary' : 'text-gray-500',
              )
            }
          >
            {({ isActive }) => (
              <>
                <item.icon className={cn('w-6 h-6 mb-1', isActive && 'stroke-[2.5px]')} />
                <span className="text-xs font-semibold">{t(item.labelKey)}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

// Exported for tests + the DashboardLayout — if we ever want the
// Sidebar to mirror the bottom nav on desktop, the role → tab keys
// map is the single source of truth.
export { ROLE_TABS, NAV_ITEMS, DEFAULT_TABS };
